import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';

/**
 * Label, input and message: the one field every auth form uses.
 *
 * - `error` is text under the field; it is announced and sets aria-invalid.
 * - `invalid` marks the field red without a message of its own (the form's AuthAlert says why);
 *   `describedBy` is that alert's id.
 * - `hint` is quieter help that shows while there is no error.
 * - `aside` sits on the label row (the "Forgot password?" link).
 * - `star` draws the required marker; `required` is only the HTML attribute.
 * - A password field gets a show/hide button.
 * Everything else goes to the <input>.
 */
function AuthField({
  id,
  label,
  type = 'text',
  required = false,
  star = false,
  invalid = false,
  error = '',
  hint = '',
  describedBy = '',
  aside = null,
  ...inputProps
}) {
  const { t } = useI18n();
  const [shown, setShown] = useState(false);
  const isPassword = type === 'password';
  const messageId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  const described = [messageId, invalid && describedBy ? describedBy : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="auth-field">
      <div className="auth-field-row">
        <label htmlFor={id}>
          {label}
          {star && <span className="auth-req" aria-hidden="true"> *</span>}
        </label>
        {aside}
      </div>

      <div className={`auth-input-wrap${isPassword ? ' has-toggle' : ''}`}>
        <input
          id={id}
          type={isPassword && shown ? 'text' : type}
          required={required}
          aria-invalid={error || invalid ? 'true' : undefined}
          aria-describedby={described}
          {...inputProps}
        />
        {isPassword && (
          <button
            type="button"
            className="auth-eye"
            onClick={() => setShown((value) => !value)}
            aria-label={shown ? t('auth.hidePassword') : t('auth.showPassword')}
            aria-pressed={shown}
          >
            {shown ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
          </button>
        )}
      </div>

      {error ? (
        <p id={`${id}-error`} className="auth-field-msg" role="alert">{error}</p>
      ) : hint ? (
        <p id={`${id}-hint`} className="auth-field-hint">{hint}</p>
      ) : null}
    </div>
  );
}

export default AuthField;
